import React, { useMemo } from 'react';
import {
  Box,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';

import { formatCurrency } from '../../../shared/utils/formatters';

const ACCENT = '#4ce1b6';
const HOTEL_COLOR = '#6366f1';
const RESTAURANT_COLOR = '#f59e0b';

const EMPTY_BREAKDOWN = {
  hotel: { label: 'Hotel', collected: 0, refunds: 0, net: 0, share_percent: 0 },
  restaurant: { label: 'Restaurant', collected: 0, refunds: 0, net: 0, share_percent: 0 },
  total_collected: 0,
  total_refunds: 0,
  total_net: 0,
};

function SourceMetric({ label, amount, share, color, icon: Icon, isDark, subtitle }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2.5,
        height: '100%',
        minHeight: 124,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        border: `1px solid ${alpha(color, isDark ? 0.35 : 0.28)}`,
        background: isDark
          ? `linear-gradient(145deg, ${alpha(color, 0.16)} 0%, ${alpha('#0f172a', 0.55)} 100%)`
          : `linear-gradient(145deg, ${alpha(color, 0.08)} 0%, #ffffff 100%)`,
        boxShadow: isDark ? '0 10px 28px rgba(0,0,0,0.22)' : '0 10px 24px rgba(15,23,42,0.06)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: `0 16px 32px ${alpha(color, 0.18)}`,
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.25}>
        <Box>
          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, letterSpacing: 0.6 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.35, color: isDark ? '#f8fafc' : '#0f172a' }}>
            {formatCurrency(amount)}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', display: 'block', mt: 0.35 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(color, 0.16),
            color,
          }}
        >
          <Icon fontSize="small" />
        </Box>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
        <Typography variant="caption" sx={{ fontWeight: 700, color }}>
          Share of total
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 800, color: isDark ? '#e2e8f0' : '#334155' }}>
          {share.toFixed(1)}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, share))}
        sx={{
          height: 8,
          borderRadius: 99,
          bgcolor: alpha(color, isDark ? 0.14 : 0.1),
          '& .MuiLinearProgress-bar': {
            borderRadius: 99,
            background: `linear-gradient(90deg, ${alpha(color, 0.85)} 0%, ${color} 100%)`,
          },
        }}
      />
    </Paper>
  );
}

export default function CashCollectionBreakdown({ breakdown, openingBreakdown, isDark, loading }) {
  const data = breakdown || EMPTY_BREAKDOWN;
  const hotel = data.hotel || EMPTY_BREAKDOWN.hotel;
  const restaurant = data.restaurant || EMPTY_BREAKDOWN.restaurant;
  const totalCollected = Number(data.total_collected || 0);

  const pieSeries = useMemo(() => {
    if (totalCollected <= 0) {
      return [{ id: 0, value: 1, label: 'No data', color: isDark ? '#475569' : '#cbd5e1' }];
    }
    return [
      { id: 'hotel', value: hotel.collected, label: 'Hotel', color: HOTEL_COLOR },
      { id: 'restaurant', value: restaurant.collected, label: 'Restaurant', color: RESTAURANT_COLOR },
    ].filter((row) => row.value > 0);
  }, [hotel.collected, restaurant.collected, totalCollected, isDark]);

  const barDataset = useMemo(
    () => [
      { source: 'Hotel', collected: hotel.collected, color: HOTEL_COLOR },
      { source: 'Restaurant', collected: restaurant.collected, color: RESTAURANT_COLOR },
    ],
    [hotel.collected, restaurant.collected],
  );
  const barMax = Math.max(1, hotel.collected, restaurant.collected);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${alpha(ACCENT, 0.15)}` }}>
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.75 },
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        border: `1px solid ${alpha(ACCENT, isDark ? 0.2 : 0.24)}`,
        background: isDark
          ? 'linear-gradient(155deg, rgba(8,20,16,0.92) 0%, rgba(15,23,42,0.88) 55%, rgba(8,20,16,0.95) 100%)'
          : 'linear-gradient(155deg, #ffffff 0%, #f0fdf9 48%, #ffffff 100%)',
        boxShadow: isDark ? '0 18px 40px rgba(0,0,0,0.28)' : '0 16px 36px rgba(15,118,110,0.08)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: isDark
            ? `radial-gradient(circle at 85% 10%, ${alpha(ACCENT, 0.12)} 0%, transparent 42%)`
            : `radial-gradient(circle at 88% 8%, ${alpha(ACCENT, 0.16)} 0%, transparent 40%)`,
        }}
      />
      <Stack spacing={2.5} sx={{ position: 'relative', zIndex: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
          <Box>
            <Typography variant="overline" sx={{ color: ACCENT, fontWeight: 800, letterSpacing: 1.2 }}>
              Cash Collection Mix
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.25 }}>
              Hotel vs Restaurant
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Separate cash inflow by source with combined total
            </Typography>
          </Box>
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 1.25,
              borderRadius: 2,
              border: `1px solid ${alpha(ACCENT, 0.35)}`,
              bgcolor: alpha(ACCENT, isDark ? 0.1 : 0.12),
              minWidth: 180,
            }}
          >
            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
              Total Collected
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, color: ACCENT, lineHeight: 1.1 }}>
              {formatCurrency(totalCollected)}
            </Typography>
            {(data.total_refunds || 0) > 0 && (
              <Typography variant="caption" sx={{ color: isDark ? '#fca5a5' : '#dc2626' }}>
                Refunds: {formatCurrency(data.total_refunds)}
              </Typography>
            )}
          </Paper>
        </Stack>

        {openingBreakdown && Number(openingBreakdown.total || 0) >= 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${alpha('#6366f1', isDark ? 0.28 : 0.22)}`,
              bgcolor: isDark ? alpha('#6366f1', 0.08) : alpha('#6366f1', 0.04),
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Opening Float
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Typography variant="body2">
                  Hotel: <strong>{formatCurrency(openingBreakdown.hotel)}</strong>
                </Typography>
                <Typography variant="body2">
                  Restaurant: <strong>{formatCurrency(openingBreakdown.restaurant)}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: ACCENT, fontWeight: 800 }}>
                  Total: {formatCurrency(openingBreakdown.total)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ minWidth: 0, display: 'flex' }}>
            <Stack spacing={1.5} sx={{ width: '100%', height: '100%', minHeight: { md: 336 } }}>
              <SourceMetric
                label="Hotel Collection"
                amount={hotel.collected}
                share={hotel.share_percent}
                color={HOTEL_COLOR}
                icon={HotelOutlinedIcon}
                isDark={isDark}
                subtitle={hotel.refunds > 0 ? `Refunds ${formatCurrency(hotel.refunds)}` : 'Booking & front desk cash'}
              />
              <SourceMetric
                label="Restaurant Collection"
                amount={restaurant.collected}
                share={restaurant.share_percent}
                color={RESTAURANT_COLOR}
                icon={RestaurantOutlinedIcon}
                isDark={isDark}
                subtitle={restaurant.refunds > 0 ? `Refunds ${formatCurrency(restaurant.refunds)}` : 'POS & restaurant cash'}
              />
              <Paper
                elevation={0}
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  minHeight: 72,
                  display: 'flex',
                  alignItems: 'center',
                  border: `1px dashed ${alpha(ACCENT, 0.45)}`,
                  bgcolor: alpha(ACCENT, isDark ? 0.08 : 0.06),
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PaymentsOutlinedIcon sx={{ color: ACCENT, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      Combined Total
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: ACCENT }}>
                    {formatCurrency(totalCollected)}
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          </Box>

          <Box sx={{ minWidth: 0, display: 'flex' }}>
            <Box
              sx={{
                width: '100%',
                height: '100%',
                minHeight: { xs: 280, md: 336 },
                p: { xs: 2, md: 2.5 },
                borderRadius: 2.5,
                border: `1px solid ${alpha(ACCENT, isDark ? 0.14 : 0.18)}`,
                bgcolor: isDark ? alpha('#0f172a', 0.45) : alpha('#ffffff', 0.82),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: 300,
                  mx: 'auto',
                  position: 'relative',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <PieChart
                  width={300}
                  height={300}
                  margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                  series={[
                    {
                      data: pieSeries,
                      innerRadius: 72,
                      outerRadius: 118,
                      cx: 150,
                      cy: 150,
                      cornerRadius: 8,
                      paddingAngle: 3,
                      arcLabel: (item) => {
                        if (item.label === 'No data') return '';
                        if (item.id === 'hotel') return `${Number(hotel.share_percent || 0).toFixed(1)}%`;
                        if (item.id === 'restaurant') return `${Number(restaurant.share_percent || 0).toFixed(1)}%`;
                        return `${((item.value / (totalCollected || 1)) * 100).toFixed(1)}%`;
                      },
                      arcLabelMinAngle: 24,
                      valueFormatter: (item) =>
                        item.label === 'No data' ? item.label : `${item.label}: ${formatCurrency(item.value)}`,
                    },
                  ]}
                  slotProps={{ legend: { hidden: true } }}
                  sx={{
                    maxWidth: '100%',
                    '& .MuiPieArcLabel-root': {
                      fill: isDark ? '#f8fafc' : '#0f172a',
                      fontWeight: 800,
                      fontSize: 12,
                    },
                    '& .MuiPieArc-root': {
                      filter: isDark ? 'drop-shadow(0 4px 10px rgba(0,0,0,0.45))' : 'drop-shadow(0 4px 8px rgba(15,23,42,0.12))',
                    },
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
                      Total Cash
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: ACCENT }}>
                      {formatCurrency(totalCollected)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ minWidth: 0, display: 'flex' }}>
            <Box
              sx={{
                width: '100%',
                height: '100%',
                minHeight: { xs: 280, md: 336 },
                p: 1.5,
                borderRadius: 2.5,
                border: `1px solid ${alpha(ACCENT, isDark ? 0.14 : 0.18)}`,
                bgcolor: isDark ? alpha('#0f172a', 0.45) : alpha('#ffffff', 0.82),
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Collection Comparison
              </Typography>
              <BarChart
                dataset={barDataset}
                height={292}
                margin={{ top: 16, right: 12, bottom: 28, left: 52 }}
                xAxis={[
                  {
                    scaleType: 'band',
                    dataKey: 'source',
                    categoryGapRatio: 0.35,
                    tickLabelStyle: {
                      fill: isDark ? '#cbd5e1' : '#475569',
                      fontWeight: 700,
                      fontSize: 12,
                    },
                  },
                ]}
                yAxis={[
                  {
                    min: 0,
                    max: barMax * 1.15,
                    tickLabelStyle: {
                      fill: isDark ? '#94a3b8' : '#64748b',
                      fontSize: 11,
                    },
                    valueFormatter: (value) => formatCurrency(value),
                  },
                ]}
                series={[
                  {
                    dataKey: 'collected',
                    label: 'Collected',
                    color: ACCENT,
                    valueFormatter: (value) => formatCurrency(value),
                  },
                ]}
                barLabel="value"
                sx={{
                  '& .MuiBarElement-root:nth-of-type(1)': { fill: HOTEL_COLOR },
                  '& .MuiBarElement-root:nth-of-type(2)': { fill: RESTAURANT_COLOR },
                  '& .MuiBarLabel-root': {
                    fill: isDark ? '#e2e8f0' : '#0f172a',
                    fontWeight: 700,
                    fontSize: 11,
                  },
                  '& .MuiBarElement-root': {
                    borderRadius: 10,
                    filter: isDark ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.35))' : 'drop-shadow(0 3px 6px rgba(15,23,42,0.12))',
                  },
                }}
                slotProps={{ legend: { hidden: true } }}
              />
              <Stack direction="row" spacing={2} justifyContent="center" mt={0.5}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: HOTEL_COLOR }} />
                  <Typography variant="caption" color="text.secondary">Hotel</Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: RESTAURANT_COLOR }} />
                  <Typography variant="caption" color="text.secondary">Restaurant</Typography>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
}
